import React from 'react'
import { toast as displayToast } from 'sonner'

import {
  oneLowercaseLetterRegEx,
  oneNumberRegEx,
  oneSymbolRegEx,
  oneUppercaseLetterRegEx,
} from '@/components/forms/PasswordVerifier'
import { Alert } from '@/components/ui/Alert'

const ERROR_DEFAULT = 'An error occurred. Please try again.'
const ERROR_NO_INTERNET = 'Request could not be made. Please check your internet connection.'
const ERROR_TIMEOUT = 'Request timed out. Please try again.'

export const createWithDoc = ({ envName = '', docFunction = () => {}, component = '' }) => {
  let createComponentWithDoc
  if (envName !== 'production') {
    createComponentWithDoc = docFunction(component) // eslint-disable-line global-require
  }
  return createComponentWithDoc || component
}

/**
 * True when an axios error is the result of an aborted/canceled request (via
 * AbortController). These are intentional supersedes, not real failures, so
 * callers should bail silently rather than surface an error.
 */
export const isCanceledError = (err) =>
  err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError'

export const getErrorMessage = (err) => {
  // Session-expiry was already surfaced by the response interceptor; skip the
  // generic fallback so callers don't double-toast.
  if (err?.sessionExpired) return null

  // - Axios does not return a response object if the service cannot be reached
  if (err.code === 'ERR_NETWORK') {
    return ERROR_NO_INTERNET
  }

  if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
    return ERROR_TIMEOUT
  }

  // - If we do have a response and data object, use the full error object
  if (err.response && err.response.data) {
    try {
      if (typeof err.response.data === 'string') {
        return err.response.data.startsWith('<!DOCTYPE html>') ? ERROR_DEFAULT : err.response.data
      }

      const errorStringOrObject = Object.values(err.response.data)[0]
      if (typeof errorStringOrObject === 'object') return errorStringOrObject[0]
      return errorStringOrObject
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Could not infer error message from error response object')
    }
  }

  // If a message has not been set, fallback to our default message
  return ERROR_DEFAULT
}

/**
 * Updates the state object with the updated field.
 * @param {object} state
 * @param {object} updatedField
 */
export const updateState = (state, updatedField) => ({
  ...state,
  ...updatedField,
})

/**
 * Updates an array stored in state.
 * - `add`: adds value to the array
 * - `remote`: removes the value at the index from the array
 * - `update`: updates the value at the index in the array
 * - `reset` and `filter`: returns the supplied value
 * - `clear`: removes all values from the array
 * @param {object} state
 * @param {object} updatedState
 */
export const updateArrayState = (state, { type, value, index }) => {
  switch (type) {
    case 'add':
      return [...state, value]
    case 'remove': {
      const updatedState = [...state]
      updatedState.splice(index, 1)
      return updatedState
    }
    case 'update': {
      const updatedState = [...state]
      updatedState[index] = value
      return updatedState
    }
    case 'reset':
    case 'filter':
      return value
    case 'clear':
      return []
    default:
      return state
  }
}

export const joinClassNames = (...classes) => classes.filter(Boolean).join(' ')

export const toast = (message, type, options = {}) => {
  if (!message) return null
  const dedupeId = typeof message === 'string' ? { id: message } : {}
  return displayToast.custom(
    (id) => <Alert message={message} type={type} onClose={() => displayToast.dismiss(id)} />,
    { ...dedupeId, ...options },
  )
}

export const stripHtmlTags = (html) => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  return tempDiv.textContent.trim() || tempDiv.innerText.trim()
}

export const normalizeHtml = (html) => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  // Extract meaningful text content while ignoring attributes
  return tempDiv.textContent.trim()
}

/**
 * Handles updating pagination based on the supplied parameters and functions.
 * @param {number} page
 * @param {number} currentPage
 * @param {number} perPage
 * @param {number} totalRows
 * @param {object} pages
 * @param {func} setCurrentPage
 * @param {func} request
 * @param {string} baseUrl
 * @param {string} filter
 */
export const handlePagination = async (
  page,
  currentPage,
  perPage,
  totalRows,
  pages,
  setCurrentPage,
  request,
  baseUrl,
  filter = null,
) => {
  let response
  // If the user is requesting the first page and we are not on the next page,
  // we need to get the very first page and not utilize `previous`.
  if (page === 1 && currentPage > 1) {
    response = await request(`${baseUrl}${perPage}&${filter ? `${filter}&` : ''}page=1`)
  }
  // If the user is requesting the last page and we are not on the previous page,
  // we need to get the very last page and not utilize `next`.
  else if (page > currentPage && page - currentPage > 1) {
    response = await request(
      `${baseUrl}${perPage}&${filter ? `${filter}&` : ''}page=${Math.ceil(totalRows / perPage)}`,
    )
  }
  // If the user is requesting the next page.
  else if (page > currentPage) {
    response = await request(pages.next)
  }
  // Otherwise, the user is requesting the previous page.
  else {
    response = await request(pages.previous)
  }

  // Only advance the page indicator when the request actually completed. A
  // superseded (aborted) request resolves to null/undefined; advancing the page
  // on it would desync `currentPage` from the loaded data, and with
  // react-data-table-component's `paginationDefaultPage={currentPage}` binding
  // that re-fires `onChangePage` — turning rapid clicks into a request loop.
  if (response) setCurrentPage(page)

  return response
}

/**
 * Handles verifying the password against requirements.
 * @param {string} value
 * @returns {string} error if there is one to display
 */
export const verifyPassword = (value) => {
  if (!value || value.length < 8) return 'Please enter more than 8 characters'
  if (!oneUppercaseLetterRegEx.test(value)) return 'Please enter at least 1 uppercase letter'
  if (!oneLowercaseLetterRegEx.test(value)) return 'Please enter at least 1 lowercase letter'
  if (!oneNumberRegEx.test(value)) return 'Please enter at least 1 number'
  if (!oneSymbolRegEx.test(value)) return 'Please enter at least 1 symbol'
  return undefined
}

/**
 * Formats the specified `phoneNumber`.
 * @param string phoneNumber
 * @returns
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (phoneNumber) {
    const cleaned = ('', phoneNumber).replace(/\D/g, '')
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)

    if (match) {
      const intlCode = match[1] ? '+1 ' : ''
      return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
    }
  }
  return ''
}

/**
 * Handles verifying the currency input value.
 * @param {object} e
 * @param {func} setError
 * @param {func} clearErrors
 * @param {boolean} allowNegative - Whether to allow negative values (default: false)
 * @returns rawValue
 */
export const verifyCurrencyInputValue = (e, setError, clearErrors, allowNegative = false) => {
  let rawValue = e.target.value

  const decimalCount = (rawValue.match(/\./g) || []).length
  const hasMultipleDecimals = decimalCount > 1

  if (hasMultipleDecimals) {
    setError('additionalDollarsHeldUp', {
      type: 'custom',
      message: 'Cannot have multiple decimals',
    })
    // Return without updating the value to prevent adding a second decimal
    return null
  }
  clearErrors('additionalDollarsHeldUp')

  // Remove non-numeric characters except for the decimal point and optionally the minus sign
  if (allowNegative) {
    rawValue = rawValue.replace(/[^0-9.-]/g, '').replace(/(?!^)-/g, '')
  } else {
    rawValue = rawValue.replace(/[^0-9.]/g, '')
  }

  // Do not automatically format to two decimal places unless user inputs them
  // Only convert to a number if the input is a valid number without unnecessary formatting
  const numericValue = parseFloat(rawValue)

  if (!Number.isNaN(numericValue) && rawValue.includes('.')) {
    // Preserve user input without forcing two decimal places unless they input them
    const decimalIndex = rawValue.indexOf('.')
    const decimalPart = rawValue.substring(decimalIndex + 1)

    if (decimalPart.length > 2) {
      // If more than two decimal places are entered, truncate to two decimal places
      rawValue = rawValue.substring(0, decimalIndex + 3)
    }
  }

  return rawValue
}

/**
 * Configures the address based on the updated dealer.
 */
export const configureDealerAddress = (dealer) => {
  if (!dealer) return ''
  let address = ''
  if (dealer.city) {
    address += `${dealer.city}, `
  }
  if (dealer.state) {
    address += `${dealer.state} `
  }
  if (dealer.postalCode) {
    address += dealer.postalCode
  }

  return address
}
